import React, { useState, useEffect } from "react";
import http from "@shared/api/http";
import { useTranslation } from "react-i18next";
import { Phone } from "@mui/icons-material";

export default function TherapistMap({ hasActiveTherapist = false, currentTherapist = null }) {
  const { t } = useTranslation();
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // removed unused showList and selectedTherapist states

  const url = "/de/v2/:token/accounts";
  useEffect(() => {
    const fetchTherapists = async () => {
      try {
        setLoading(true);
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };
        const response = await http.get(url, { headers });
        
        if (response.data && Array.isArray(response.data)) {
          // Filter therapists with valid coordinates
          const validTherapists = response.data.filter(
            therapist => therapist.latitude && therapist.longitude
          );
          setTherapists(validTherapists);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching therapists:", err);
        setError(true);
        setLoading(false);
      }
    };

    fetchTherapists();
  }, [url]);


  // Function to handle therapist selection
  const selectTherapist = (index) => {
    const therapist = therapists[index];
    
    // Send message to iframe to pan to the selected therapist
    setTimeout(() => {
      const iframe = document.querySelector('iframe[title="Interactive Therapist Map"]');
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'PAN_TO_THERAPIST',
          therapist: therapist
        }, '*');
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">{t("loading_map")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning" role="alert">
        {t("unable_to_load_locations")}
      </div>
    );
  }

  // Calculate center point for the map - prioritize current therapist if available
  let centerTherapist = null;
  let centerLat = 47.5833; // Default to Kufstein, Tyrol, Austria
  let centerLng = 12.1667;
  
  if (currentTherapist && therapists.length > 0) {
    // Try to find the current therapist in the full therapists list by matching email or clinic name
    centerTherapist = therapists.find(t => 
      (t.email && currentTherapist.email && t.email.toLowerCase() === currentTherapist.email.toLowerCase()) ||
      (t.clinic_name && currentTherapist.clinic_name && t.clinic_name.toLowerCase() === currentTherapist.clinic_name.toLowerCase())
    );
  }
  
  // If we found the current therapist with coordinates, use them; otherwise use first therapist or default
  if (centerTherapist && centerTherapist.latitude && centerTherapist.longitude) {
    centerLat = parseFloat(centerTherapist.latitude);
    centerLng = parseFloat(centerTherapist.longitude);
    console.log('Using current therapist coordinates:', centerLat, centerLng, centerTherapist.clinic_name);
  } else if (therapists.length > 0 && therapists[0].latitude && therapists[0].longitude) {
    centerLat = parseFloat(therapists[0].latitude);
    centerLng = parseFloat(therapists[0].longitude);
    console.log('Using first therapist coordinates:', centerLat, centerLng, therapists[0].clinic_name);
  } else {
    console.log('Using default coordinates (Kufstein, Austria):', centerLat, centerLng);
  }


  // Create a map with individual markers using a different approach
  const createMapWithMarkers = () => {
    // The map lives in an iframe, which doesn't inherit the brand's CSS
    // variables, so the colour has to be baked into its HTML.
    const mainColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--main-color")
        .trim() || "#84bd00";
    // Create a data URL with embedded map and markers
    const mapHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Therapist Map</title>
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
        <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
        <style>
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
          #map { height: 100vh; width: 100%; }
          .custom-popup { font-size: 12px; min-width: 200px; }
          .custom-popup h6 { margin: 0 0 8px 0; color: #333; font-size: 16px; font-weight: bold; }
          .custom-popup p { margin: 3px 0; color: #666; font-size: 11px; }
          .custom-popup .address { font-size: 12px; margin-bottom: 8px; }
          .custom-popup .contact { font-size: 11px; margin: 2px 0; }
          .custom-popup .google-maps-btn { 
            background: ${mainColor} !important;
            color: white !important; 
            padding: 6px 12px !important; 
            text-decoration: none !important; 
            border-radius: 4px !important; 
            font-size: 11px !important;
            display: inline-block !important;
            margin-top: 8px !important;
            border: none !important;
          }
          .custom-popup .google-maps-btn:hover { 
            background: color-mix(in srgb, ${mainColor}, black 15%) !important;
            color: white !important;
          }
        </style>
      </head>
      <body>
        <div id="map"></div>
        <script>
          // Initialize map - zoom in more for current therapist
          var map = L.map('map').setView([${centerLat}, ${centerLng}], 10);
          
          // Add tile layer
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(map);
          
          // Therapist data
          var therapists = ${JSON.stringify(therapists)};
          
          // Create custom icon with main color
          var customIcon = L.divIcon({
            className: 'custom-marker',
            html: '<div style="background-color: ${mainColor}; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          });
          
          // Add markers for each therapist
          var markers = [];
          therapists.forEach(function(therapist, index) {
            if (therapist.latitude && therapist.longitude) {
              var marker = L.marker([parseFloat(therapist.latitude), parseFloat(therapist.longitude)], {
                icon: customIcon
              }).addTo(map);
              
              // Create popup content with better styling
              var popupContent = '<div class="custom-popup">' +
                '<h6>' + (therapist.clinic_name || 'Therapist') + '</h6>' +
                '<p class="address"><strong>Address:</strong><br>' + 
                (therapist.street || '') + '<br>' +
                (therapist.zip || '') + ' ' + (therapist.city || '') + '<br>' +
                (therapist.country || '') + '</p>';
              
              if (therapist.phone) {
                popupContent += '<p class="contact"><strong>Phone:</strong> ' + therapist.phone + '</p>';
              }
              
              if (therapist.email) {
                popupContent += '<p class="contact"><strong>Email:</strong> ' + therapist.email + '</p>';
              }
              
              popupContent += '<a href="https://www.google.com/maps/search/?api=1&query=' + 
                therapist.latitude + ',' + therapist.longitude + '" target="_blank" ' +
                'class="google-maps-btn">' + '${t("menu.click_to_view_on_map")}' + '</a>' +
                '</div>';
              
              marker.bindPopup(popupContent);
              markers.push(marker);
            }
          });
          
          // Fit map to show all markers, but keep current therapist centered
          if (markers.length > 0) {
            var group = new L.featureGroup(markers);
            var bounds = group.getBounds();
            // If we have a current therapist with coordinates, don't auto-fit, keep them centered
            var hasCurrentTherapist = ${centerTherapist ? 'true' : 'false'};
            if (!hasCurrentTherapist) {
              map.fitBounds(bounds.pad(0.1));
            }
          }
          
          // Listen for messages from parent window to pan to specific therapist
          window.addEventListener('message', function(event) {
            if (event.data && event.data.type === 'PAN_TO_THERAPIST' && event.data.therapist) {
              var therapist = event.data.therapist;
              if (therapist.latitude && therapist.longitude) {
                var lat = parseFloat(therapist.latitude);
                var lng = parseFloat(therapist.longitude);
                
                // Pan to the therapist location with animation
                map.setView([lat, lng], 15, {
                  animate: true,
                  duration: 1.0
                });
                
                // Open the popup for that therapist
                markers.forEach(function(marker) {
                  if (Math.abs(marker.getLatLng().lat - lat) < 0.0001 && Math.abs(marker.getLatLng().lng - lng) < 0.0001) {
                    marker.openPopup();
                  }
                });
              }
            }
          });
          
        </script>
      </body>
      </html>
    `;

    return `data:text/html;charset=utf-8,${encodeURIComponent(mapHtml)}`;
  };

  return (
    <div className="mt-4">
      <div className="text-center mb-4">
        <h5>{t("menu.all_public_therapists")}</h5>
      </div>
      
      <div style={{ 
        height: "500px", 
        width: "100%", 
        border: "1px solid #ddd", 
        borderRadius: "8px",
        overflow: "hidden"
      }}>
        <iframe
          src={createMapWithMarkers()}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          title="Interactive Therapist Map"
        />
      </div>



      {/* Collapsible therapist list */}
      <div className="mt-3">
        <details>
          <summary 
            className="text-muted" 
            style={{ cursor: "pointer", fontSize: "14px", fontWeight: "500" }}
          >
            {t("menu.show_therapist_locations")} ({therapists.length})
          </summary>
          <div className="mt-2">
            <div className="row">
              {therapists.map((therapist, index) => (
                <div key={index} className="col-md-6 col-lg-4 mb-2">
                  <div 
                    className="card h-100" 
                    style={{ 
                      cursor: "pointer",
                      transition: "transform 0.2s",
                    }}
                    onMouseEnter={(e) => e.target.style.transform = "scale(1.02)"}
                    onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                    onClick={() => selectTherapist(index)}
                  >
                    <div className="card-body p-3">
                      <h6 className="card-title mb-1">{therapist.clinic_name}</h6>
                      <p className="card-text mb-1">
                        <small className="text-muted">
                          {therapist.street}<br />
                          {therapist.zip} {therapist.city}
                        </small>
                      </p>
                      {therapist.phone && (
                        <p className="card-text mb-1">
                          <small><Phone style={{ fontSize: "12px", marginRight: "4px", color: "#6c757d" }} /> {therapist.phone}</small>
                        </p>
                      )}
                      <div className="mt-2">
                        <span 
                          className="badge" 
                          style={{ 
                            backgroundColor: "var(--main-color, #84bd00)", 
                            color: "white",
                            border: "none"
                          }}
                        >
                          {t("menu.click_to_view_on_map")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}